import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MyPrivacySettings, UserInfo, UserUpdate } from '@ca/core/models';
import { UserNotificationService, UserService } from '@ca/core/services';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { EMPTY, Observable } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, tap } from 'rxjs/operators';
import { CustomValidators } from '../../../../../utils/custom-validators/custom-validators';
import * as FormUtils from '../../../../../utils/form/form-utils';
import { COUNTRIES } from '../../../../shared/constants/countries';

@Component({
    selector: 'ca-profile-editor',
    templateUrl: './profile-editor.component.html',
    styleUrls: ['./profile-editor.component.scss'],
})
export class ProfileEditorComponent implements OnInit {
    imageUrl: string = '';
    myPrivacySettings: MyPrivacySettings;

    formGroup: UntypedFormGroup;
    isSubmitting: boolean = false;

    user: UserInfo;

    constructor(
        private formBuilder: UntypedFormBuilder,
        protected userService: UserService,
        private userNotificationService: UserNotificationService,
        private activeModal: NgbActiveModal,
    ) {}

    ngOnInit(): void {
        const { id, title, firstName, lastName, organization, occupation, phoneNumber, website, country, city, description } = this.user;
        this.formGroup = this.formBuilder.group({
            id: id,
            title: [title, CustomValidators.CommonName()],
            firstName: [firstName, [Validators.required, CustomValidators.CommonName()]],
            lastName: [lastName, [Validators.required, CustomValidators.CommonName()]],
            organization: [organization, CustomValidators.CommonName()],
            occupation: [occupation, CustomValidators.CommonName()],
            phoneNumber: [phoneNumber, CustomValidators.PhoneNumber()],
            website: [website],
            country: country,
            city: [city, CustomValidators.CommonName()],
            description: description,
        });

        this.userService
            .getMyPrivacySettings$()
            .pipe(
                map((privacySettings: MyPrivacySettings) => {
                    this.myPrivacySettings = privacySettings;

                    const { email, showEmail } = this.myPrivacySettings;

                    this.formGroup.addControl('email', new UntypedFormControl({ value: email, disabled: true }));
                    this.formGroup.addControl('showEmail', new UntypedFormControl(showEmail));
                }),
            )
            .subscribe();
    }

    searchCountries(text$: Observable<string>): Observable<string[]> {
        return text$.pipe(
            debounceTime(200),
            distinctUntilChanged(),
            map(term => (term.length < 2 ? [] : COUNTRIES.filter(v => v.toLowerCase().includes(term.toLowerCase())))),
        );
    }

    dismiss(reason: string): void {
        this.activeModal.dismiss(reason);
    }

    submitProfile(): void {
        // Add default value null because the empty value is invalid input, use null to clear.
        if (FormUtils.isValidForm(this.formGroup)) {
            let formValues: UserUpdate = this.formGroup.value;
            formValues = FormUtils.trimWhitespacesOfFormValues(formValues);
            formValues = FormUtils.transformEmptyStringsToNullInFormValues(formValues);

            const { title, firstName, lastName, organization, occupation, phoneNumber, website, country, city, description, email, showEmail } = formValues;

            const { headerImage, picture } = this.user;

            this.isSubmitting = true;

            const payload: UserUpdate = {
                title: title,
                firstName: firstName,
                lastName: lastName,
                organization: organization,
                occupation: occupation,
                phoneNumber: phoneNumber,
                website: website,
                country: country,
                city: city,
                description: description,
                headerImage: headerImage,
                picture: picture,
                email: email,
                showEmail: showEmail || false,
            };

            this.myPrivacySettings.showEmail = showEmail || false;

            this.userNotificationService.showSpinner();
            this.userService
                .updateUserProfile$(payload)
                .pipe(
                    tap((user: UserInfo) => {
                        this.isSubmitting = false;
                        this.userNotificationService.hideSpinner();
                        this.user = user;
                        this.userNotificationService.showSuccess('GENERAL.CHANGED_SUCCESS');
                        const result = { data: this.user, myPrivacySettings: this.myPrivacySettings };
                        this.activeModal.close(result);
                    }),
                    catchError((err: HttpErrorResponse) => {
                        this.userNotificationService.hideSpinner();
                        console.error('Failed to save your changes.', err);
                        this.isSubmitting = false;
                        this.userNotificationService.showError('GENERAL.CHANGED_FAILURE');
                        return EMPTY;
                    }),
                )
                .subscribe();
        }
    }
}
